import { type Event } from '@kalos/kalos-rpc';
import { addDays, addMinutes, differenceInCalendarDays, format, isValid, parseISO } from 'date-fns';

export type JobLength = 'single' | 'multi';

export type DateTimeRange = { start: Date; end: Date };

// Multi-day starts the morning of the picked day and ends the next evening.
const DEFAULT_START_TIME = '08:00';
const DEFAULT_END_TIME = '18:00';

const parseEventDateTime = (raw?: string): Date | null => {
  const trimmed = raw?.trim();
  if (!trimmed) return null;
  const d = parseISO(trimmed.replace(' ', 'T'));
  return isValid(d) ? d : null;
};

/** Calendar days the range touches, counting both ends: Oct 22 to Oct 25 is 4. */
export const countCalendarDays = (start: Date, end: Date): number =>
  differenceInCalendarDays(end, start) + 1;

/** "4 days · Thu, Oct 22 to Sun, Oct 25". Null when the range is invalid or backwards. */
export const formatDayRange = (start: Date, end: Date): string | null => {
  if (!isValid(start) || !isValid(end)) return null;
  const days = countCalendarDays(start, end);
  if (days < 1) return null;
  const label = `${days} ${days === 1 ? 'day' : 'days'}`;
  if (days === 1) return `${label} · ${format(start, 'EEE, MMM d')}`;
  return `${label} · ${format(start, 'EEE, MMM d')} to ${format(end, 'EEE, MMM d')}`;
};

/** The range a new multi-day job starts with, for the day picked in the scheduler (yyyy-MM-dd). */
export const defaultMultiDayRange = (date: string): DateTimeRange => {
  const start = parseISO(`${date}T${DEFAULT_START_TIME}`);
  const end = parseISO(`${format(addDays(parseISO(date), 1), 'yyyy-MM-dd')}T${DEFAULT_END_TIME}`);
  return { start, end };
};

/**
 * The saved job's range when it ends on a later calendar day than it starts, else null.
 * Reads dateStarted/dateEnded the way First Calls does for "Day X of Y".
 */
export const existingMultiDayRange = (
  event?: Partial<Pick<Event, 'dateStarted' | 'dateEnded'>>,
): DateTimeRange | null => {
  const start = parseEventDateTime(event?.dateStarted);
  const end = parseEventDateTime(event?.dateEnded);
  if (!start || !end) return null;
  return differenceInCalendarDays(end, start) > 0 ? { start, end } : null;
};

const minutesOfDay = (d: Date) => d.getHours() * 60 + d.getMinutes();

/** Moving the start moves the end by the same days and clock time, across daylight saving too. */
export const moveRangeStart = (range: DateTimeRange, start: Date): DateTimeRange => ({
  start,
  end: addMinutes(
    addDays(range.end, differenceInCalendarDays(start, range.start)),
    minutesOfDay(start) - minutesOfDay(range.start),
  ),
});

/** "yyyy-MM-dd HH:mm", the shape the job-info edit panel saves dateStarted/dateEnded in. */
export const toEventDateTime = (d: Date): string => format(d, 'yyyy-MM-dd HH:mm');
